import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Event } from "../../../services/EventService";
import { CalendarDays, Link, Search, Unlink, AlertCircle, CheckCircle, X, Check, User } from 'lucide-react';

interface SideEventsManagerProps {
  mainEventId: string;
  userId: string;
  onUpdate?: () => void;
}

interface LinkedEvent {
  id: string;
  title: string;
  start_date: string;
  link_status?: 'pending' | 'accepted' | 'rejected';
}

interface OutgoingLink {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  invitation_message?: string | null;
  requested_by: string;
  decided_by?: string | null;
  side_event: { id: string; title: string; start_date: string };
  requester?: { id: string; username?: string; full_name?: string; avatar_url?: string };
  decider?: { id: string; username?: string; full_name?: string };
}

interface IncomingLink {
  id: string;
  status: 'pending';
  invitation_message?: string | null;
  requested_by: string;
  main_event: { id: string; title: string; start_date: string };
  requester?: { id: string; username?: string; full_name?: string; avatar_url?: string };
  created_at: string;
}

export const SideEventsManager = ({ mainEventId, userId, onUpdate }: SideEventsManagerProps) => {
  // State
  const [outgoingLinks, setOutgoingLinks] = useState<OutgoingLink[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<IncomingLink[]>([]);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [activeTab, setActiveTab] = useState<'outgoing' | 'incoming'>('outgoing');

  const showMessage = (message: string, error: boolean = false) => {
    setStatusMessage(message);
    setIsError(error);
    setTimeout(() => setStatusMessage(''), 3500);
  };

  const fetchOutgoing = async () => {
    if (!mainEventId) return;
    const { data, error } = await Event.getOutgoingSideLinks(mainEventId);
    if (error) {
      showMessage("Error loading side events", true);
      return;
    }
    setOutgoingLinks(data as any || []);
  };

  const fetchIncoming = async () => {
    if (!mainEventId) return;
    const { data, error } = await Event.getIncomingSideLinkRequests(mainEventId);
    if (error) {
      showMessage("Error loading requests", true);
      return;
    }
    setIncomingRequests(data as any || []);
  };

  const refreshAll = async () => {
    await Promise.all([fetchOutgoing(), fetchIncoming()]);
  };

  const handleSearch = async () => {
    if (!searchTerm) return;
    setSearching(true);
    const { data, error } = await Event.searchEventsLinking(searchTerm);
    setSearching(false);
    if (error) {
      showMessage("Failed to search events", true);
      return;
    }
    const filtered = (data || []).filter(e => e.id !== mainEventId);
    setSearchResults(filtered);
    if (filtered.length === 0) {
      showMessage("No matching events found", true);
    }
  };

  const handleLinkEvent = async (sideEventId: string) => {
    const { data, error } = await Event.requestSideEventLink(mainEventId, sideEventId, invitationMessage.trim() || undefined);
    if (error) {
      showMessage(error.message || "Error creating link", true);
      return;
    }
    showMessage(
      data?.status === 'pending'
        ? 'Request sent (pending approval)'
        : 'Side event linked'
    );
    setInvitationMessage('');
    setSearchTerm('');
    setSearchResults([]);
    refreshAll();
    onUpdate?.();
  };

  const handleCancelLink = async (linkId: string) => {
    const { error } = await Event.cancelSideEventLink(linkId);
    if (error) {
      showMessage(error.message || "Error cancelling", true);
      return;
    }
    showMessage("Link cancelled");
    refreshAll();
    onUpdate?.();
  };

  const handleDecision = async (linkId: string, decision: 'accepted' | 'rejected') => {
    const { error } = await Event.decideSideEventLink(linkId, decision);
    if (error) {
      showMessage(error.message || "Error updating status", true);
      return;
    }
    showMessage(decision === 'accepted' ? 'Request accepted' : 'Request rejected');
    refreshAll();
    onUpdate?.();
  };

  useEffect(() => {
    if (mainEventId) {
      refreshAll();
    }
  }, [mainEventId]);

  const statusBadge = (status: OutgoingLink['status']) => {
    const map: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-rose-100 text-rose-700',
      cancelled: 'bg-gray-200 text-gray-600'
    };
    const label: Record<string, string> = {
      pending: 'PENDING',
      accepted: 'ACCEPTED',
      rejected: 'REJECTED',
      cancelled: 'CANCELLED'
    };
    return (
      <span className={`text-[10px] rounded px-2 py-0.5 font-medium ${map[status]}`}>
        {label[status]}
      </span>
    );
  };

  const requesterLabel = (r?: { username?: string; full_name?: string; id: string }) =>
    r?.full_name || r?.username || (r?.id ? r.id.slice(0, 8) : '—');

  return (
    <div className="space-y-4">
      {statusMessage && (
        <div className={`p-3 rounded-md flex items-center gap-2 ${
          isError ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
        }`}>
          {isError ? <AlertCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
          <span className="text-sm">{statusMessage}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Side Events</h3>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-xs"
            onKeyDown={(e) => { if (e.key === 'Enter' && searchTerm) handleSearch(); }}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleSearch}
            disabled={searching || !searchTerm}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-muted-foreground">
          Invitation message (optional)
        </label>
        <textarea
          className="w-full text-sm border rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-violet-200"
          rows={2}
          value={invitationMessage}
          onChange={(e) => setInvitationMessage(e.target.value)}
          placeholder="E.g. We would like to show your meetup as a side event of the festival..."
        />
      </div>

      {searchResults.length > 0 && (
        <div className="border rounded-md p-2 bg-slate-50">
          <h4 className="text-sm font-medium mb-2">Results</h4>
          <div className="space-y-2">
            {searchResults.map(event => (
              <div key={event.id} className="flex items-center justify-between p-2 bg-white rounded border">
                <div>
                  <h5 className="font-medium">{event.title}</h5>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <CalendarDays className="h-3 w-3" />
                    {new Date(event.start_date).toLocaleDateString()}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleLinkEvent(event.id)}
                  style={{color: '#6D29F6', borderColor: '#6D29F6'}}
                >
                  <Link className="h-3 w-3 mr-1" /> Invite
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          size="sm"
            variant={activeTab === 'outgoing' ? 'default' : 'outline'}
          onClick={() => setActiveTab('outgoing')}
        >
          Outgoing
        </Button>
        <Button
          size="sm"
          variant={activeTab === 'incoming' ? 'default' : 'outline'}
          onClick={() => setActiveTab('incoming')}
        >
          Incoming {incomingRequests.length > 0 && (
            <span className="ml-1 text-[10px] bg-amber-500 text-white rounded px-1">
              {incomingRequests.length}
            </span>
          )}
        </Button>
      </div>

      {activeTab === 'outgoing' && (
        <div className="border rounded-md p-4">
          <h4 className="text-sm font-medium mb-2">Side Events (Outgoing)</h4>
          {outgoingLinks.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No side events linked yet
            </div>
          ) : (
            <div className="space-y-2">
              {outgoingLinks.map(link => (
                <div key={link.id} className="p-3 border rounded bg-white">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h5 className="font-medium">{link.side_event.title}</h5>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarDays className="h-3 w-3" />
                          {new Date(link.side_event.start_date).toLocaleDateString()}
                        </div>
                        {statusBadge(link.status)}
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          Invited by: {requesterLabel(link.requester) === userId ? 'You' : requesterLabel(link.requester)}
                        </div>
                      </div>
                      {link.invitation_message && (
                        <p className="text-xs text-muted-foreground border-l-2 border-violet-300 pl-2 mt-1 italic">
                          “{link.invitation_message}”
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      {(link.status === 'pending' || link.status === 'accepted') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCancelLink(link.id)}
                          className="justify-center"
                          style={{color: '#FF5C5F', borderColor: '#FF5C5F'}}
                        >
                          <Unlink className="h-3 w-3 mr-1" /> Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'incoming' && (
        <div className="border rounded-md p-4">
          <h4 className="text-sm font-medium mb-2">Incoming Requests</h4>
          {incomingRequests.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No pending requests
            </div>
          ) : (
            <div className="space-y-2">
              {incomingRequests.map(req => (
                <div key={req.id} className="p-3 border rounded bg-white">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h5 className="font-medium">{req.main_event.title}</h5>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarDays className="h-3 w-3" />
                          {new Date(req.main_event.start_date).toLocaleDateString()}
                        </div>
                        <span className="text-[10px] rounded px-2 py-0.5 font-medium bg-amber-100 text-amber-700">
                          PENDING
                        </span>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          Requested by: {requesterLabel(req.requester)}
                        </div>
                      </div>
                      {req.invitation_message && (
                        <p className="text-xs text-muted-foreground border-l-2 border-amber-300 pl-2 mt-1 italic">
                          “{req.invitation_message}”
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDecision(req.id, 'accepted')}
                        className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                      >
                        <Check className="h-3 w-3 mr-1" /> Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDecision(req.id, 'rejected')}
                        className="border-rose-500 text-rose-600 hover:bg-rose-50"
                      >
                        <X className="h-3 w-3 mr-1" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancelLink(req.id)}
                        className="border-gray-400 text-gray-600 hover:bg-gray-50"
                      >
                        <Unlink className="h-3 w-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};